import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import http from "../../../api/apiClient";
import { getInitialSchoolYearId } from "../../../components/SchoolYearDropdown";
import { teacherSchoolTasksApi } from "../api";
import { parseSchoolYear, parseValidList } from "../parsers";
import type {
  Child,
  Classroom,
  CleaningAssignment,
  CleaningMoment,
  Family,
  SchoolActivity,
  SchoolTask,
  SchoolTaskSignup,
  SchoolTaskStatus,
  SchoolYear,
} from "../types";

export type SchoolTasksAdminDataSection = "tasks" | "signups" | "cleaning";
export type SchoolTasksAdminDataErrors = Partial<Record<SchoolTasksAdminDataSection, string>>;

export interface SchoolTasksAdminPermissions {
  canViewTasks: boolean;
  canViewSignups: boolean;
  canViewCleaning: boolean;
  canListYears: boolean;
  canListClassrooms: boolean;
  canListFamilies: boolean;
}

export interface UseSchoolTasksAdminDataOptions {
  permissions: SchoolTasksAdminPermissions;
  yearId: string;
}

export interface SchoolTasksAdminReferenceData {
  years: SchoolYear[];
  classrooms: Classroom[];
  families: Family[];
  familyChildren: Record<string, Child[]>;
  suggestedYearId: string;
}

export interface SchoolTasksAdminYearData {
  tasks: SchoolTask[];
  activities: SchoolActivity[];
  signups: SchoolTaskSignup[];
  moments: CleaningMoment[];
  assignments: CleaningAssignment[];
  statuses: SchoolTaskStatus[];
}

export interface SchoolTasksAdminDataResult {
  reference: SchoolTasksAdminReferenceData;
  year: SchoolTasksAdminYearData;
  loading: { base: boolean; year: boolean };
  errors: {
    base?: string;
    lookup: SchoolTasksAdminDataErrors;
    section: SchoolTasksAdminDataErrors;
  };
  refreshBase(): Promise<void>;
  refreshYear(): Promise<void>;
}

const emptyReference: SchoolTasksAdminReferenceData = {
  years: [],
  classrooms: [],
  families: [],
  familyChildren: {},
  suggestedYearId: "",
};

const emptyYear: SchoolTasksAdminYearData = {
  tasks: [],
  activities: [],
  signups: [],
  moments: [],
  assignments: [],
  statuses: [],
};

export function useSchoolTasksAdminData({ permissions, yearId }: UseSchoolTasksAdminDataOptions): SchoolTasksAdminDataResult {
  const { t } = useTranslation();
  const [reference, setReference] = useState<SchoolTasksAdminReferenceData>(emptyReference);
  const [year, setYear] = useState<SchoolTasksAdminYearData>(emptyYear);
  const [baseLoading, setBaseLoading] = useState(true);
  const [yearLoading, setYearLoading] = useState(false);
  const [baseError, setBaseError] = useState<string>();
  const [lookupErrors, setLookupErrors] = useState<SchoolTasksAdminDataErrors>({});
  const [sectionErrors, setSectionErrors] = useState<SchoolTasksAdminDataErrors>({});
  const [familiesForStatuses, setFamiliesForStatuses] = useState<Family[]>([]);
  const mounted = useRef(true);
  const baseGeneration = useRef(0);
  const yearGeneration = useRef(0);
  const currentYearId = useRef(yearId);
  currentYearId.current = yearId;

  const {
    canViewTasks,
    canViewSignups,
    canViewCleaning,
    canListYears,
    canListClassrooms,
    canListFamilies,
  } = permissions;

  useEffect(() => () => {
    mounted.current = false;
    baseGeneration.current += 1;
    yearGeneration.current += 1;
  }, []);

  const refreshBase = useCallback(async () => {
    const generation = ++baseGeneration.current;
    const current = () => mounted.current && generation === baseGeneration.current;
    const loadError = t("schoolTasks.errors.load", { defaultValue: "Could not load school tasks." });
    const needYears = canListYears && (canViewTasks || canViewSignups || canViewCleaning);
    const needClassrooms = canListClassrooms && canViewTasks;
    const needFamilies = canListFamilies && (canViewSignups || canViewCleaning);

    setBaseLoading(true);
    setBaseError(undefined);
    setLookupErrors({});
    const [yearResult, classroomResult, familyResult] = await Promise.allSettled([
      needYears ? http.get<SchoolYear[]>("/years") : Promise.resolve({ data: [] }),
      needClassrooms ? http.get<Classroom[]>("/classrooms") : Promise.resolve({ data: [] }),
      needFamilies ? http.get<Family[]>("/families") : Promise.resolve({ data: [] }),
    ]);
    if (!current()) return;

    const years = yearResult.status === "fulfilled" ? parseValidList(yearResult.value.data, parseSchoolYear) : [];
    const classrooms = classroomResult.status === "fulfilled" && Array.isArray(classroomResult.value.data) ? classroomResult.value.data : [];
    const families = familyResult.status === "fulfilled" && Array.isArray(familyResult.value.data) ? familyResult.value.data : [];
    const nextLookupErrors: SchoolTasksAdminDataErrors = {};
    if (needClassrooms && classroomResult.status === "rejected") nextLookupErrors.tasks = loadError;
    if (needFamilies && familyResult.status === "rejected") {
      if (canViewSignups) nextLookupErrors.signups = loadError;
      if (canViewCleaning) nextLookupErrors.cleaning = loadError;
    }

    let familyChildren: Record<string, Child[]> = {};
    if (needFamilies && familyResult.status === "fulfilled") {
      const pairs = await Promise.all(families.map(async (family) => {
        try {
          const response = await http.get(`/child-family/by-family/${family.id}`);
          const children = (Array.isArray(response.data) ? response.data : []).map((link: any) => link.child || link);
          const childrenWithClassrooms = await Promise.all(children.map(async (child: Child) => {
            if (!needClassrooms) return child;
            try {
              const assignments = await http.get(`/child-classrooms/classrooms-for-child/${child.id}`);
              return { ...child, classroomAssignments: Array.isArray(assignments.data) ? assignments.data : [] };
            } catch {
              return { ...child, classroomAssignments: [] };
            }
          }));
          return [family.id, childrenWithClassrooms] as const;
        } catch {
          return [family.id, []] as const;
        }
      }));
      if (!current()) return;
      familyChildren = Object.fromEntries(pairs);
    }

    const selectedYearId = currentYearId.current;
    const suggestedYearId = years.some((schoolYear) => schoolYear.id === selectedYearId)
      ? selectedYearId
      : getInitialSchoolYearId(years);
    setReference({ years, classrooms, families, familyChildren, suggestedYearId });
    setFamiliesForStatuses(families);
    setBaseError(needYears && yearResult.status === "rejected" ? loadError : undefined);
    setLookupErrors(nextLookupErrors);
    setBaseLoading(false);
  }, [canListClassrooms, canListFamilies, canListYears, canViewCleaning, canViewSignups, canViewTasks, t]);

  const refreshYear = useCallback(async () => {
    const generation = ++yearGeneration.current;
    const current = () => mounted.current && generation === yearGeneration.current;
    const loadError = t("schoolTasks.errors.load", { defaultValue: "Could not load school tasks." });
    setYear(emptyYear);
    setSectionErrors({});
    if (!yearId) {
      setYearLoading(false);
      return;
    }
    setYearLoading(true);

    const [taskResult, activityResult, signupResult, momentResult, assignmentResult] = await Promise.allSettled([
      canViewTasks ? teacherSchoolTasksApi.tasks(yearId) : Promise.resolve([]),
      canViewTasks ? teacherSchoolTasksApi.activities(yearId) : Promise.resolve([]),
      canViewSignups ? teacherSchoolTasksApi.signups() : Promise.resolve([]),
      canViewCleaning ? teacherSchoolTasksApi.moments(yearId) : Promise.resolve([]),
      canViewCleaning ? teacherSchoolTasksApi.assignments() : Promise.resolve([]),
    ]);
    if (!current()) return;

    const nextErrors: SchoolTasksAdminDataErrors = {};
    const tasks = taskResult.status === "fulfilled" ? taskResult.value || [] : [];
    const activities = activityResult.status === "fulfilled" ? activityResult.value || [] : [];
    const moments = momentResult.status === "fulfilled" ? momentResult.value || [] : [];
    if (taskResult.status === "rejected" && canViewTasks) nextErrors.tasks = loadError;
    if (activityResult.status === "rejected" && canViewTasks) nextErrors.tasks = loadError;
    if (momentResult.status === "rejected" && canViewCleaning) nextErrors.cleaning = loadError;

    let signups: SchoolTaskSignup[] = [];
    if (signupResult.status === "fulfilled" && taskResult.status === "fulfilled") {
      const taskIds = new Set(tasks.map((task) => task.id));
      signups = (signupResult.value || []).filter((signup) => taskIds.has(signup.taskId));
    } else if (signupResult.status === "rejected" && canViewSignups) nextErrors.signups = loadError;

    let assignments: CleaningAssignment[] = [];
    if (assignmentResult.status === "fulfilled" && momentResult.status === "fulfilled") {
      const momentIds = new Set(moments.map((moment) => moment.id));
      assignments = (assignmentResult.value || []).filter((assignment) => momentIds.has(assignment.momentId));
    } else if (assignmentResult.status === "rejected" && canViewCleaning) nextErrors.cleaning = loadError;

    setYear({ tasks, activities, signups, moments, assignments, statuses: [] });
    if (canViewSignups) {
      const statusResults = await Promise.allSettled(
        familiesForStatuses.map((family) => teacherSchoolTasksApi.status(family.id, yearId)),
      );
      if (!current()) return;
      const statuses = statusResults
        .filter((result): result is PromiseFulfilledResult<SchoolTaskStatus> => result.status === "fulfilled")
        .map((result) => result.value);
      setYear((previous) => ({ ...previous, statuses }));
      if (statusResults.some((result) => result.status === "rejected")) nextErrors.signups = loadError;
    }
    setSectionErrors(nextErrors);
    setYearLoading(false);
  }, [canViewCleaning, canViewSignups, canViewTasks, familiesForStatuses, t, yearId]);

  useEffect(() => { void refreshBase(); }, [refreshBase]);
  useEffect(() => { void refreshYear(); }, [refreshYear]);

  return {
    reference: {
      ...reference,
      suggestedYearId: reference.years.some((schoolYear) => schoolYear.id === yearId)
        ? yearId
        : getInitialSchoolYearId(reference.years),
    },
    year,
    loading: { base: baseLoading, year: yearLoading },
    errors: { base: baseError, lookup: lookupErrors, section: sectionErrors },
    refreshBase,
    refreshYear,
  };
}
